import { Component, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { first } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { ReportType } from '@interfaces/schema';
import { Session } from '@providers/session/session.service';
import { Logger } from '../../utils/logger';
import { DeeplinksService } from './deeplinks.service';

/**
 * Deeplinks Component.
 *
 * @exports
 * @class DeeplinksComponent
 */
@Component({
  selector: 'app-deeplinks',
  template: ``,
  standalone: true,
  providers: [DeeplinksService]
})
export class DeeplinksComponent {
  private readonly router = inject(Router);
  private readonly service = inject(DeeplinksService);
  private readonly session = inject(Session);
  private readonly zone = inject(NgZone);

  private readonly logger = Logger.getInstance(DeeplinksComponent.name);

  /**
   * Creates an instance of DeeplinksComponent.
   *
   * @memberof DeeplinksComponent
   */
  constructor() {
    this.setup();
  }

  /**
   * Setup deeplinks.
   *
   * Testing:
   * - Android: sign and upload to internal app sharing.
   * - IOS: can use simulator.
   *
   * @memberof DeeplinksComponent
   */
  setup(): void {
    const logger = this.logger.child(this.setup.name);

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      logger.log('App opened with URL:', JSON.stringify(event));

      this.zone.run(async () => {
        // FOR URL INFORMATION PLEASE SEE README.md on this folder.
        const url = new URL(event.url);
        if (url.pathname) {
          let route = '';
          const queryParams: any = {};

          url.searchParams.forEach((value, key) => {
            queryParams[key] = value;
          });

          if (url.pathname.includes('/shop')) {
            route = '/tabs/shop';
          } else if (url.pathname.includes('/user/order/shipping')) {
            route = '/shop/order/shipping';
            queryParams.backTo = '/home';
          } else if (url.pathname.includes('/user/order/success')) {
            const id: string = queryParams.id;
            route = `/shop/checkout/payment/success/${id}`;
          } else if (url.pathname.includes('/user/order')) {
            const id: string = queryParams.id;
            route = `/order/${id}`;
            queryParams.backTo = '/home';
          } else if (url.pathname.includes('/account/delete')) {
            route = `/profile/settings`;
            queryParams.backTo = '/home';
          } else if (url.pathname.includes('/products/dna-360')) {
            const reports = await firstValueFrom(
              this.service.fetchDNAReports({
                where: {
                  reportType: ReportType.Dna_360
                },
                limit: 1
              })
            );
            const dnaReport = first(reports.results);
            if (dnaReport?.releasedAt) {
              route = '/report/360';
            } else {
              route = '/shop';
              queryParams.sortByReportType = ReportType.Dna_360;
            }
          }

          logger.log(
            this.session.isAuthenticated(),
            route,
            JSON.stringify(queryParams)
          );

          if (route) {
            this.router.navigate([route], { queryParams, replaceUrl: true });
          }
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
  }
}
